import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';
import { PRICING_CONFIG, PLANS, PRO_ANNUAL_PRICE_PER_USER, PRO_MIN_USERS } from '../config/pricing';

export default function PricingPage() {
  const location = useLocation();
  const { isSignedIn } = useAuth();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const founderConfig = PRICING_CONFIG[PLANS.FOUNDER];
  const founderPrice = founderConfig.price.launch ?? founderConfig.price.standard;
  const proConfig = PRICING_CONFIG[PLANS.PRO];
  const founderCTADest = isSignedIn ? '/dashboard' : '/sign-up';
  const proCTADest = isSignedIn ? '/dashboard' : '/sign-up';
  const comparisons = [
    ['Company foundation and business analysis', true, true],
    ['Strategic objectives and plan summary', true, true],
    ['Visual organization chart', true, true],
    ['AI strategy generation and diagnostics', false, true],
    ['Projects, tasks and delegation', false, true],
    ['Project and task progress', false, true],
  ] as const;

  return (
    <MarketingLayout>
      <div className="public-breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Pricing' }]} />
        </div>
      </div>
      <PageHero
        heroImageUrl={heroImageUrl}
        title="Start with clarity. Add execution when you’re ready."
        subtitle="Shape your company’s direction with Founder. Activate Pro to connect that plan to your team’s projects, tasks and accountability."
      >
        <div className="flex flex-wrap justify-center gap-4">
          <a href="#plans" className="public-button public-button--primary">
            Find your plan <ArrowRight size={18} aria-hidden="true" />
          </a>
          <Link to="/contact" className="public-button public-button--secondary">
            Talk to our team
          </Link>
        </div>
      </PageHero>

      <section id="plans" className="public-section public-section--tint scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="public-card flex flex-col p-7 md:p-9"
            >
              <span className="self-start rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 mb-5">
                Build your strategic foundation
              </span>
              <h2 className="text-3xl font-bold mb-2">{founderConfig.name}</h2>
              <p className="text-gray-600 mb-7">{founderConfig.tagline}</p>
              <div className="mb-7">
                <div className="flex items-baseline gap-3 flex-wrap">
                  {founderConfig.price.launch && (
                    <span className="text-2xl text-gray-400 line-through">
                      ${founderConfig.price.standard.amount}
                    </span>
                  )}
                  <span className="text-5xl md:text-6xl font-bold tracking-tight">
                    ${founderPrice.amount}
                  </span>
                  <span className="text-gray-600">
                    {founderConfig.price.launch ? 'for Year 1' : '/ year'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  {founderConfig.price.launch &&
                    `Renews at $${founderConfig.price.standard.amount}/year. `}
                  Prices in {founderConfig.price.standard.currency}.
                </p>
              </div>
              <div className="space-y-6 mb-8">
                <div>
                  <h3 className="font-semibold mb-3">A clear plan for your company</h3>
                  <ul className="space-y-3 text-sm text-gray-600">
                    {founderConfig.features.included.map((feature) => (
                      <li key={feature} className="flex gap-3">
                        <CheckCircle
                          className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0"
                          aria-hidden="true"
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t border-gray-100 pt-5">
                  <h3 className="font-semibold mb-3">Activate Pro when you need execution</h3>
                  <ul className="space-y-2 text-sm text-gray-500">
                    {founderConfig.features.excluded.map((feature) => (
                      <li key={feature} className="flex gap-3">
                        <X className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <Link
                to={founderCTADest}
                className="public-button public-button--primary w-full mt-auto"
              >
                {isSignedIn ? 'Subscribe Now' : 'Start with Founder'}{' '}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </motion.div>

            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="public-card public-card--purple flex flex-col p-7 md:p-9"
            >
              <span className="self-start rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700 mb-5">
                Requires Founder
              </span>
              <h2 className="text-3xl font-bold mb-2">{proConfig.name}</h2>
              <p className="text-gray-600 mb-7">{proConfig.tagline}</p>
              <div className="mb-7">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-2xl text-gray-400 line-through">
                    ${proConfig.price.standard.amount}
                  </span>
                  <span className="text-5xl md:text-6xl font-bold tracking-tight">
                    ${proConfig.price.launch?.amount}
                  </span>
                  <span className="text-gray-600">/ user / month</span>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  Year 1 launch price. Renews at ${proConfig.price.standard.amount}/user/month.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Minimum {PRO_MIN_USERS} users. Annual option:{' '}
                  <span className="line-through text-gray-400">
                    ${PRO_ANNUAL_PRICE_PER_USER.standard}
                  </span>{' '}
                  ${PRO_ANNUAL_PRICE_PER_USER.launch}/user/year for Year 1.
                </p>
              </div>
              <h3 className="font-semibold mb-3">Put your strategy to work, together</h3>
              <ul className="space-y-3 text-sm text-gray-600 mb-8">
                {proConfig.features.included.map((feature) => (
                  <li key={feature} className="flex gap-3">
                    <CheckCircle
                      className="h-4 w-4 text-violet-600 mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {feature === 'KPI tracking and execution monitoring'
                      ? 'Project and task progress'
                      : feature}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600 mb-6">
                Pro Activation adds execution to your Founder subscription.
              </p>
              <Link to={proCTADest} className="public-button public-button--primary w-full mt-auto">
                {isSignedIn ? 'Subscribe Now' : proConfig.cta.primary}{' '}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </motion.div>
          </div>
          <p className="text-center text-sm text-gray-600 mt-8 max-w-3xl mx-auto">
            Both plans include the strategic planning framework, security features and regular
            updates.
          </p>
        </div>
      </section>

      <section className="public-section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="public-section-heading text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                The right layer for your next step.
              </h2>
              <p className="text-lg text-gray-600">
                Build the plan first. Bring your leadership team into execution when you’re ready.
              </p>
            </div>
            <div className="public-card overflow-x-auto">
              <table className="w-full min-w-[580px] text-left text-sm">
                <caption className="sr-only">
                  Founder and Founder with Pro Activation feature comparison
                </caption>
                <thead className="bg-blue-50">
                  <tr>
                    <th scope="col" className="p-5 font-semibold">
                      What you can do
                    </th>
                    <th scope="col" className="p-5 font-semibold">
                      Founder
                    </th>
                    <th scope="col" className="p-5 font-semibold">
                      Founder + Pro
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisons.map(([feature, founder, pro]) => (
                    <tr key={feature} className="border-t border-gray-100">
                      <th scope="row" className="p-5 font-medium">
                        {feature}
                      </th>
                      <td className="p-5 text-blue-700">{founder ? 'Included' : 'Add Pro'}</td>
                      <td className="p-5 text-blue-700">{pro ? 'Included' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--tint">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="public-section-heading text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                A few things to know before you start.
              </h2>
            </div>
            <div className="space-y-4">
              <details className="public-card p-6">
                <summary className="font-semibold cursor-pointer">
                  Do I need Founder before activating Pro?
                </summary>
                <p className="text-gray-600 mt-4">
                  Yes. Founder is your company’s strategic planning foundation. Pro Activation adds
                  AI strategy tools, projects, tasks and accountability to that subscription.
                </p>
              </details>
              <details className="public-card p-6">
                <summary className="font-semibold cursor-pointer">
                  How does the Year 1 launch price work?
                </summary>
                <p className="text-gray-600 mt-4">
                  {`Founder is $${founderConfig.price.launch?.amount} for your first year and renews at $${founderConfig.price.standard.amount}/year. Pro’s monthly launch rate is $${proConfig.price.launch?.amount}/user/month for Year 1, renewing at $${proConfig.price.standard.amount}/user/month. The annual Pro option is $${PRO_ANNUAL_PRICE_PER_USER.launch}/user for Year 1 and renews at $${PRO_ANNUAL_PRICE_PER_USER.standard}/user/year.`}
                </p>
              </details>
              <details className="public-card p-6">
                <summary className="font-semibold cursor-pointer">
                  How many companies and people are included?
                </summary>
                <p className="text-gray-600 mt-4">
                  Founder includes {founderConfig.limits.companies} company and{' '}
                  {founderConfig.limits.users} user. Pro Activation has a minimum of {PRO_MIN_USERS}{' '}
                  users so your leadership team can work on execution together.
                </p>
              </details>
              <details className="public-card p-6">
                <summary className="font-semibold cursor-pointer">
                  How do I choose and activate my plan?
                </summary>
                <p className="text-gray-600 mt-4">
                  Create your account or sign in, then select your plan from your dashboard. If you
                  need help choosing,{' '}
                  <Link to="/contact" className="text-blue-600 underline">
                    contact our team
                  </Link>
                  .
                </p>
              </details>
            </div>
          </div>
        </div>
      </section>
      <section className="public-cta">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose a plan with confidence.</h2>
          <p className="text-lg text-gray-600 mb-7">
            Tell us about your company and the work you want to connect.
          </p>
          <Link to="/contact" className="public-button public-button--primary">
            Talk to our team <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
